import { z } from 'zod/v4';
import { trpc, RouterOutputs } from '@org/clients/school';
import {
  Button,
  DynamicForm,
  Input,
  LazyMedia,
  MediaUploader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  TextArea,
  unwrapTrpcData,
} from '@org/ui';
import { XIcon, Route as RouteIcon } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import { toast } from 'sonner';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getMutationErrorMessage } from '../shared/multi-step-utils';
import {
  DEFAULT_TRIAL_DAYS,
  findOtherActiveBase,
  isProductFieldVisible,
  normalizeKindFields,
  productKindError,
  pickDefaultPath,
  formatPathJourneys,
} from '../product/product-form-rules';

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
const MAX_GALLERY_IMAGES = 4;
const MAX_OUTCOMES = 8;

// A product sells one journey (kind 'journey') or the base access — the whole
// default learning path, paid monthly (kind 'base', no journey). Prices are
// stored in cents; the form shows a decimal amount and converts on change so
// the payload matches the `products.create` / `products.update` input.
const productSchema = z
  .object({
  productId: z.string().optional(),
  name: z.string().min(1, 'Name is required').describe('products.form.labels.name'),
  kind: z.enum(['journey', 'base']).default('journey'),
  billing: z.enum(['one_time', 'monthly']).default('one_time'),
  journeyId: z.string().optional().nullable(),
  tagline: z.string().max(120, 'Tagline must be 120 characters or less').optional().nullable(),
  description: z
    .string()
    .max(2000, 'Description must be 2000 characters or less')
    .describe('products.form.labels.description')
    .optional()
    .nullable(),
  defaultDurationDays: z
    .number()
    .int('Duration must be a whole number of days')
    .min(1, 'Duration must be at least 1 day')
    .nullable()
    .optional(),
  // Base product only: free days a validated self-learning student gets.
  trialDays: z.number().int('Whole days only').min(0).max(365).nullable().optional(),
  priceCents: z.number().int().min(0, 'Price cannot be negative').default(0),
  currency: z
    .string()
    .trim()
    .length(3, 'Currency must be a 3-letter code (e.g. BRL)')
    .default('BRL'),
  // Storefront fields (poster wall + product page). All optional.
  coverImageUrl: z.string().url('Must be a valid URL').optional().nullable().or(z.literal('')),
  galleryImageUrls: z.array(z.string().url()).max(MAX_GALLERY_IMAGES).default([]),
  audience: z.string().max(2000).optional().nullable(),
  outcomes: z.array(z.string().max(160, 'Each line must be 160 characters or less')).max(MAX_OUTCOMES).default([]),
  teacherNote: z.string().max(2000).optional().nullable(),
  isActive: z.boolean().default(true),
  })
  .superRefine((values, ctx) => {
    if (productKindError(values) === 'journeyRequired') {
      ctx.addIssue({ code: 'custom', path: ['journeyId'], message: 'Journey is required' });
    }
  });

type ProductFormValues = z.infer<typeof productSchema>;
type JourneyRow = RouterOutputs['journeys']['getJourneys'][number];
type PathListRow = { pathId: string; name: string; isDefault: boolean; journeyCount: number };
type ProductRow = RouterOutputs['products']['list'][number];

type FieldContext = {
  field: { value: unknown; onChange: (value: unknown) => void };
  methods: { watch: (name: string) => unknown };
};

export interface ProductFormProps {
  defaultValues: any;
  onSubmit: (data: any) => void;
  onClose: () => void;
}

// Local text state lets the admin type "49." without the value snapping
// back; the form field only ever holds the integer cents.
function PriceInput({ field, currency }: { field: FieldContext['field']; currency: string }) {
  const { t } = useTranslation('admin');
  const cents = typeof field.value === 'number' ? field.value : 0;
  const [text, setText] = useState(() => (cents / 100).toFixed(2));

  return (
    <div className="flex flex-col gap-1">
      <Input
        type="number"
        inputMode="decimal"
        min={0}
        step="0.01"
        value={text}
        onChange={(event) => {
          const next = event.target.value;
          setText(next);
          const parsed = Number.parseFloat(next);
          field.onChange(Number.isFinite(parsed) ? Math.max(0, Math.round(parsed * 100)) : 0);
        }}
        onBlur={() => setText((cents / 100).toFixed(2))}
      />
      <span className="text-xs text-muted-foreground">
        {t('products.form.hints.price', { currency })}
      </span>
    </div>
  );
}

export function ProductForm({ defaultValues, onSubmit, onClose }: ProductFormProps) {
  const utils = trpc.useUtils();
  const productId = defaultValues?.productId;
  const { t } = useTranslation('admin');

  const [formDefaultValues] = useState({
    isActive: true,
    priceCents: 0,
    currency: 'BRL',
    defaultDurationDays: null,
    kind: 'journey',
    billing: 'one_time',
    trialDays: null,
    ...defaultValues,
    // jsonb columns come back null on older rows; the editors expect arrays.
    galleryImageUrls: Array.isArray(defaultValues?.galleryImageUrls) ? defaultValues.galleryImageUrls : [],
    outcomes: Array.isArray(defaultValues?.outcomes) ? defaultValues.outcomes : [],
  });

  const { data: journeysData } = trpc.journeys.getJourneys.useQuery();
  // The base product has no single journey: it is the default learning path.
  // Shown read-only under "Tipo" so the admin never looks for a journey to pick.
  const { data: pathsData, isLoading: pathsLoading } = trpc.learningPaths.list.useQuery();
  const defaultPath = useMemo(
    () => pickDefaultPath(unwrapTrpcData<PathListRow[]>(pathsData as PathListRow[]) ?? []),
    [pathsData],
  );
  const { data: defaultPathDetail } = trpc.learningPaths.getById.useQuery(
    { pathId: defaultPath?.pathId ?? '' },
    { enabled: Boolean(defaultPath?.pathId) },
  );
  // Only to warn when a second active base product is being created.
  const { data: productsData } = trpc.products.list.useQuery();
  const otherActiveBase = useMemo(
    () => findOtherActiveBase(unwrapTrpcData<ProductRow[]>(productsData as ProductRow[]) ?? [], productId),
    [productsData, productId],
  );

  const journeyOptions = useMemo(() => {
    const journeys = unwrapTrpcData<JourneyRow[]>(journeysData as JourneyRow[]) ?? [];
    return journeys.map((journey) => ({
      journeyId: journey.journeyId,
      label: journey.name ?? t('journeys.dialog.unnamedJourney'),
    }));
  }, [journeysData, t]);

  const invalidate = async () => {
    await utils.products.list.invalidate();
    if (productId) {
      await utils.products.getById.invalidate({ productId });
    }
  };

  const createMutation = trpc.products.create.useMutation();
  const updateMutation = trpc.products.update.useMutation();

  const handleFormSubmit = async (data: ProductFormValues) => {
    try {
      let savedProductId: string | undefined = productId;
      const payload = {
        name: data.name,
        description: data.description ?? null,
        ...normalizeKindFields(data),
        defaultDurationDays: data.defaultDurationDays ?? null,
        priceCents: data.priceCents,
        currency: data.currency.toUpperCase(),
        isActive: data.isActive,
        coverImageUrl: data.coverImageUrl?.trim() ? data.coverImageUrl.trim() : null,
        galleryImageUrls: (data.galleryImageUrls ?? []).filter(Boolean).slice(0, MAX_GALLERY_IMAGES),
        tagline: data.tagline?.trim() ? data.tagline.trim() : null,
        audience: data.audience?.trim() ? data.audience.trim() : null,
        outcomes: (data.outcomes ?? [])
          .map((line) => line.trim())
          .filter(Boolean)
          .slice(0, MAX_OUTCOMES),
        teacherNote: data.teacherNote?.trim() ? data.teacherNote.trim() : null,
      };

      if (productId) {
        await updateMutation.mutateAsync({ productId, ...payload });
      } else {
        const created = await createMutation.mutateAsync(payload);
        savedProductId = created.productId;
      }

      await invalidate();
      toast.success(
        productId
          ? t('products.form.toasts.updateSuccess')
          : t('products.form.toasts.createSuccess'),
      );
      onSubmit({ ...data, productId: savedProductId });
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error(t('products.form.toasts.saveFailed', { message: getMutationErrorMessage(error) }));
    }
  };

  const labelMappings = {
    name: t('products.form.labels.name'),
    kind: t('products.form.labels.kind'),
    billing: t('products.form.labels.billing'),
    trialDays: t('products.form.labels.trialDays'),
    journeyId: t('products.form.labels.journeyId'),
    description: t('products.form.labels.description'),
    defaultDurationDays: t('products.form.labels.defaultDurationDays'),
    priceCents: t('products.form.labels.priceCents'),
    currency: t('products.form.labels.currency'),
    isActive: t('products.form.labels.isActive'),
    coverImageUrl: t('products.form.labels.coverImageUrl'),
    galleryImageUrls: t('products.form.labels.galleryImageUrls'),
    tagline: t('products.form.labels.tagline'),
    audience: t('products.form.labels.audience'),
    outcomes: t('products.form.labels.outcomes'),
    teacherNote: t('products.form.labels.teacherNote'),
  };

  const hiddenFields = ['productId'];

  const customRenderers = {
    // Journey product vs base access. Switching to base clears the journey
    // and proposes the default trial; switching back drops the trial.
    kind: ({ field, methods }: FieldContext) => {
      const kind = (field.value as string | undefined) ?? 'journey';
      const setValue = (methods as unknown as { setValue: (name: string, value: unknown) => void }).setValue;
      return (
        <div className="flex flex-col gap-1">
          <Select
            value={kind}
            onValueChange={(selected) => {
              field.onChange(selected);
              if (selected === 'base') {
                setValue('journeyId', null);
                setValue('billing', 'monthly');
                if (methods.watch('trialDays') == null) setValue('trialDays', DEFAULT_TRIAL_DAYS);
              } else {
                setValue('trialDays', null);
              }
            }}
          >
            <SelectTrigger className="w-full text-left">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="journey">
                <span className="flex flex-col">
                  <span>{t('products.form.kind.journey')}</span>
                  <span className="text-xs text-muted-foreground">{t('products.form.kindDescriptions.journey')}</span>
                </span>
              </SelectItem>
              <SelectItem value="base">
                <span className="flex flex-col">
                  <span>{t('products.form.kind.base')}</span>
                  <span className="text-xs text-muted-foreground">{t('products.form.kindDescriptions.base')}</span>
                </span>
              </SelectItem>
            </SelectContent>
          </Select>
          <span className="text-xs text-muted-foreground">{t('products.form.hints.kind')}</span>
          {kind === 'base' && (
            <div
              role="group"
              aria-label={t('products.form.basePath.title')}
              data-testid="base-path-panel"
              className="mt-1 rounded-md border bg-muted/40 px-3 py-2 text-sm"
            >
              {pathsLoading ? (
                <span className="text-muted-foreground">{t('products.form.basePath.loading')}</span>
              ) : defaultPath ? (
                <>
                  <div className="flex items-center gap-2 font-medium">
                    <RouteIcon className="h-4 w-4 text-muted-foreground" aria-hidden />
                    <span>
                      {t('products.form.basePath.title')}: {defaultPath.name}
                    </span>
                    <span className="text-muted-foreground">
                      · {t('products.form.basePath.journeys', { count: defaultPath.journeyCount })}
                    </span>
                  </div>
                  {defaultPathDetail?.journeys?.length ? (
                    <p className="mt-1 text-muted-foreground">
                      {formatPathJourneys(defaultPathDetail.journeys, t('journeys.dialog.unnamedJourney'))}
                    </p>
                  ) : null}
                  <p className="mt-1 text-xs text-muted-foreground">
                    {t('products.form.basePath.hint')}{' '}
                    <Link to="/learning-paths" className="underline underline-offset-2">
                      {t('products.form.basePath.editLink')}
                    </Link>
                  </p>
                </>
              ) : (
                <p role="note" className="text-amber-900">
                  {t('products.form.basePath.missing')}{' '}
                  <Link to="/learning-paths" className="underline underline-offset-2">
                    {t('products.form.basePath.editLink')}
                  </Link>
                </p>
              )}
            </div>
          )}
          {kind === 'base' && otherActiveBase && (
            <span
              role="note"
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900"
            >
              {t('products.form.hints.baseWarning', { name: otherActiveBase.name })}
            </span>
          )}
        </div>
      );
    },
    billing: ({ field }: FieldContext) => (
      <div className="flex flex-col gap-1">
        <Select
          value={(field.value as string | undefined) ?? 'one_time'}
          onValueChange={(selected) => field.onChange(selected)}
        >
          <SelectTrigger className="w-full text-left">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="one_time">{t('products.form.billing.oneTime')}</SelectItem>
            <SelectItem value="monthly">{t('products.form.billing.monthly')}</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-muted-foreground">{t('products.form.hints.billing')}</span>
      </div>
    ),
    trialDays: ({ field }: FieldContext) => (
      <div className="flex flex-col gap-1">
        <Input
          type="number"
          inputMode="numeric"
          min={0}
          max={365}
          step={1}
          value={typeof field.value === 'number' ? String(field.value) : ''}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw === '') {
              field.onChange(null);
              return;
            }
            const parsed = Number.parseInt(raw, 10);
            field.onChange(Number.isFinite(parsed) ? parsed : null);
          }}
        />
        <span className="text-xs text-muted-foreground">{t('products.form.hints.trialDays')}</span>
      </div>
    ),
    journeyId: ({ field }: FieldContext) => (
      <Select
        value={(field.value as string | undefined) || undefined}
        onValueChange={(selected) => field.onChange(selected)}
      >
        <SelectTrigger className="w-full text-left">
          <SelectValue placeholder={t('products.form.placeholders.journey')} />
        </SelectTrigger>
        <SelectContent>
          {journeyOptions.map((option) => (
            <SelectItem key={option.journeyId} value={option.journeyId}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ),
    // Blank means "never expires" and is sent as null.
    defaultDurationDays: ({ field }: FieldContext) => (
      <div className="flex flex-col gap-1">
        <Input
          type="number"
          inputMode="numeric"
          min={1}
          step={1}
          placeholder={t('products.form.placeholders.neverExpires')}
          value={typeof field.value === 'number' ? String(field.value) : ''}
          onChange={(event) => {
            const raw = event.target.value;
            if (raw === '') {
              field.onChange(null);
              return;
            }
            const parsed = Number.parseInt(raw, 10);
            field.onChange(Number.isFinite(parsed) ? parsed : null);
          }}
        />
        <span className="text-xs text-muted-foreground">{t('products.form.hints.duration')}</span>
      </div>
    ),
    priceCents: ({ field, methods }: FieldContext) => (
      <PriceInput
        field={field}
        currency={String(methods.watch('currency') ?? 'BRL').toUpperCase()}
      />
    ),
    // Poster image: same upload + lazy preview as the unit portrait.
    coverImageUrl: ({ field }: FieldContext) => {
      const value = field.value;
      if (typeof value === 'string' && value.length > 0) {
        return (
          <LazyMedia
            title={t('products.form.labels.coverImageUrl')}
            placeholder={t('products.form.cover.uploaded')}
            onReplace={() => field.onChange('')}
          >
            <div className="aspect-[3/4] max-w-[200px] overflow-hidden rounded-md bg-muted">
              <img src={value} alt="" className="h-full w-full object-cover" />
            </div>
          </LazyMedia>
        );
      }
      return (
        <div className="flex flex-col gap-1">
          <MediaUploader
            compact
            acceptedFileTypes={IMAGE_TYPES}
            title={t('products.form.cover.uploadTitle')}
            description={t('products.form.cover.uploadDescription')}
            onUploadComplete={(url) => field.onChange(url)}
          />
          <span className="text-xs text-muted-foreground">{t('products.form.hints.cover')}</span>
        </div>
      );
    },
    galleryImageUrls: ({ field }: FieldContext) => {
      const urls = Array.isArray(field.value) ? (field.value as string[]) : [];
      return (
        <div className="flex flex-col gap-2">
          {urls.length > 0 && (
            <div className="flex flex-wrap gap-3">
              {urls.map((url, index) => (
                <div key={`${url}-${index}`} className="relative h-24 w-24 overflow-hidden rounded-md border border-border bg-muted">
                  <img src={url} alt="" className="h-full w-full object-cover" />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="absolute right-1 top-1 h-6 w-6 p-0"
                    aria-label={t('products.form.gallery.remove')}
                    onClick={() => field.onChange(urls.filter((_, i) => i !== index))}
                  >
                    <XIcon className="h-3 w-3" />
                  </Button>
                </div>
              ))}
            </div>
          )}
          {urls.length < MAX_GALLERY_IMAGES && (
            <MediaUploader
              key={urls.length}
              compact
              acceptedFileTypes={IMAGE_TYPES}
              title={t('products.form.gallery.uploadTitle')}
              description={t('products.form.gallery.uploadDescription')}
              onUploadComplete={(url) => field.onChange([...urls, url].slice(0, MAX_GALLERY_IMAGES))}
            />
          )}
          <span className="text-xs text-muted-foreground">
            {t('products.form.hints.gallery', { count: urls.length, max: MAX_GALLERY_IMAGES })}
          </span>
        </div>
      );
    },
    tagline: ({ field }: FieldContext) => (
      <div className="flex flex-col gap-1">
        <Input
          type="text"
          maxLength={120}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(event) => field.onChange(event.target.value)}
          placeholder={t('products.form.placeholders.tagline')}
        />
        <span className="text-xs text-muted-foreground">{t('products.form.hints.tagline')}</span>
      </div>
    ),
    audience: ({ field }: FieldContext) => (
      <div className="flex flex-col gap-1">
        <TextArea
          rows={3}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(event) => field.onChange(event.target.value)}
          placeholder={t('products.form.placeholders.audience')}
        />
        <span className="text-xs text-muted-foreground">{t('products.form.hints.audience')}</span>
      </div>
    ),
    // One input per line, like exercise options: add / remove, max 8.
    outcomes: ({ field }: FieldContext) => {
      const lines = Array.isArray(field.value) ? (field.value as string[]) : [];
      return (
        <div className="flex flex-col gap-2">
          {lines.map((line, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                value={line}
                maxLength={160}
                placeholder={t('products.form.placeholders.outcome', { index: index + 1 })}
                onChange={(event) =>
                  field.onChange(lines.map((current, i) => (i === index ? event.target.value : current)))
                }
                className="flex-1"
              />
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="text-slate-500 hover:text-red-500"
                aria-label={t('products.form.outcomes.remove')}
                onClick={() => field.onChange(lines.filter((_, i) => i !== index))}
              >
                <XIcon className="h-4 w-4" />
              </Button>
            </div>
          ))}
          {lines.length < MAX_OUTCOMES && (
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => field.onChange([...lines, ''])}
              >
                {t('products.form.outcomes.add')}
              </Button>
            </div>
          )}
          <span className="text-xs text-muted-foreground">
            {t('products.form.hints.outcomes', { max: MAX_OUTCOMES })}
          </span>
        </div>
      );
    },
    teacherNote: ({ field }: FieldContext) => (
      <div className="flex flex-col gap-1">
        <TextArea
          rows={3}
          value={(field.value as string | null | undefined) ?? ''}
          onChange={(event) => field.onChange(event.target.value)}
          placeholder={t('products.form.placeholders.teacherNote')}
        />
        <span className="text-xs text-muted-foreground">{t('products.form.hints.teacherNote')}</span>
      </div>
    ),
  };

  return (
    <DynamicForm
      schema={productSchema}
      onSubmit={handleFormSubmit}
      defaultValues={formDefaultValues}
      isEditing={!!productId}
      onClose={onClose}
      labelMappings={labelMappings}
      hiddenFields={hiddenFields}
      fieldVisibility={({ key, values }) => isProductFieldVisible(key, values as { kind?: 'journey' | 'base' })}
      customRenderers={customRenderers as any}
    />
  );
}
